import { FC, useRef, useState } from 'react';
import {
  Box,
  Chip,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans, useTrans } from '@libs/i18n';
import { Assessment } from '@libs/types';
import { CaretDownIcon, CheckCircleIcon } from '@phosphor-icons/react';

import { DeltaTag } from './DeltaTag';

interface VersionSelectorProps {
  versions: Assessment[]; // newest-first, completed only
  activeVersionId: string;
  onSelect: (versionId: string) => void;
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
};

export const VersionSelector: FC<VersionSelectorProps> = observer(({
  versions,
  activeVersionId,
  onSelect,
}) => {
  const theme = useTheme();
  const { t } = useTrans();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const active = versions.find((v) => v.id === activeVersionId);
  if (!active || versions.length <= 1) return null;

  // Sort newest-first by version number
  const sorted = [...versions].sort((a, b) => b.version - a.version);

  return (
    <>
      {/* Trigger — circle badge + version label + date */}
      <Box
        component="button"
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.875,
          px: 0.875,
          py: 0.5,
          borderRadius: '10px',
          border: `1px solid #E8D4F8`,
          backgroundColor: '#FAF5FD',
          cursor: 'pointer',
          fontFamily: 'inherit',
          flexShrink: 0,
          '&:hover': { backgroundColor: '#F3E6FB' },
        }}
      >
        {/* Filled circle badge */}
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            backgroundColor: theme.palette.violet[700],
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 10.5,
            flexShrink: 0,
          }}
        >
          V{active.version}
        </Box>

        {/* Two-line text block */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              component="span"
              sx={{ fontWeight: 600, fontSize: 11.5, color: theme.palette.violet[700] }}
            >
              Version {active.version}
            </Typography>
            {active.isLatest && (
              <Typography
                component="span"
                sx={{ fontWeight: 400, fontSize: 10.5, color: theme.palette.violet[600] }}
              >
                · <Trans i18nKey="assessments.overview.versioning.versionSelector.latest" />
              </Typography>
            )}
          </Box>
          {active.submittedAt && (
            <Typography
              component="span"
              sx={{ fontSize: 10, color: theme.palette.violet[500], opacity: 0.85 }}
            >
              Completed {formatDate(active.submittedAt)}
            </Typography>
          )}
        </Box>

        <CaretDownIcon size={10} weight="bold" color={theme.palette.violet[700]} />
      </Box>

      <Popper
        open={open}
        anchorEl={triggerRef.current}
        placement="bottom-start"
        sx={{ zIndex: theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={0}
            sx={{
              mt: 1,
              minWidth: 360,
              maxWidth: 440,
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: 1.5,
              boxShadow: '0 12px 28px rgba(10,10,10,0.10), 0 2px 6px rgba(10,10,10,0.04)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.grey[200]}` }}>
              <Typography
                variant="textSM_semiBold"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  fontSize: 11.5,
                }}
              >
                <Trans i18nKey="assessments.overview.versioning.versionSelector.heading" />
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {sorted.map((v, i) => {
                const isActive = v.id === activeVersionId;
                const prev = sorted[i + 1];
                const score = v.score ?? null;
                const prevScore = prev?.score ?? null;
                const showDelta = score != null && prevScore != null && !!prev;
                const roundedScore = score != null ? Math.round(score) : 0;
                const roundedPrevScore = prevScore != null ? Math.round(prevScore) : 0;

                return (
                  <Box
                    key={v.id}
                    onClick={() => {
                      onSelect(v.id);
                      setOpen(false);
                    }}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr auto',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      backgroundColor: isActive ? '#FAF5FD' : 'transparent',
                      borderTop: i === 0 ? 'none' : `1px solid ${theme.palette.grey[100]}`,
                      '&:hover': { backgroundColor: isActive ? '#F3E6FB' : '#FAFAFA' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: isActive ? theme.palette.violet[700] : '#FAF5FD',
                        color: isActive ? '#fff' : theme.palette.violet[700],
                        fontWeight: 600,
                        fontSize: 12.5,
                        border: isActive ? 'none' : '1px solid #E8D4F8',
                      }}
                    >
                      V{v.version}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="textSM_semiBold" sx={{ color: 'text.primary' }}>
                          {v.title}
                        </Typography>
                        {v.isLatest && (
                          <Chip
                            size="small"
                            icon={<CheckCircleIcon size={10} weight="bold" />}
                            label={t('assessments.overview.versioning.versionSelector.latest')}
                            sx={{
                              height: 18,
                              fontSize: 10.5,
                              fontWeight: 600,
                              backgroundColor: '#E8F8EF',
                              color: theme.palette.green[700],
                              border: '1px solid #BFE8CF',
                              borderRadius: 999,
                              '& .MuiChip-label': { px: 0.625 },
                              '& .MuiChip-icon': { ml: 0.625, color: theme.palette.green[700] },
                            }}
                          />
                        )}
                        {v.version === 1 && (
                          <Chip
                            size="small"
                            label={t('assessments.overview.versioning.versionSelector.initial')}
                            sx={{
                              height: 18,
                              fontSize: 10.5,
                              fontWeight: 500,
                              backgroundColor: '#F5F5F5',
                              color: 'text.secondary',
                              border: `1px solid ${theme.palette.grey[300]}`,
                              borderRadius: 999,
                              '& .MuiChip-label': { px: 0.625 },
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                        {formatDate(v.submittedAt)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <Typography variant="textSM_semiBold" sx={{ color: 'text.primary' }}>
                        {score != null ? `${Math.round(score)}%` : '—'}
                      </Typography>
                      {showDelta && (
                        <DeltaTag
                          now={roundedScore}
                          prev={roundedPrevScore}
                          baselineLabel={`V${prev.version}`}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
});
